// libraries
import React, { useEffect, useMemo } from 'react'
import { uniq } from 'lodash'
import PropTypes from 'prop-types'
import { useFormContext, Controller } from 'react-hook-form'
import { Stack, Typography, TextField, Autocomplete, useTheme } from '@mui/material'

// queries
import { useGetFreightLaneLocationsQuery } from 'services/freightLanes'

// generic components
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'

// helpers and utils
import { CUSTOMERS_FILTERS, TRANSPORTATION_TYPE_OPTIONS } from 'utils/constants'
import { getStatesAndProvinces } from 'utils/helpers'
import { useGetCustomerQuery } from 'services/customers'
import { useDispatch, useSelector } from 'react-redux'
import { clearCarrier } from 'state/customer'

const FreightLaneBladeInformation = ({ isUpdate }) => {
  const { data: freightLaneLocationsData } = useGetFreightLaneLocationsQuery({})
  const dispatch = useDispatch()
  const theme = useTheme()
  const methods = useFormContext()
  const { control, formState, setValue } = methods
  const { errors } = formState

  const { carrier } = useSelector(state => state.customer)
  const { data: carrierData } = useGetCustomerQuery({ customerId: carrier?.id }, { skip: !carrier?.id })

  useEffect(() => {
    if (carrierData) {
      setValue('information.carrier', carrierData)
      dispatch(clearCarrier())
    }
  }, [carrierData])

  const statesAndProvinces = useMemo(() => getStatesAndProvinces(['United States', 'Canada']), [])

  const locationOptions = useMemo(
    () =>
      uniq((freightLaneLocationsData?.Items || []).map(location => location.city)).sort((a, b) => a.localeCompare(b)),
    [freightLaneLocationsData],
  )

  return (
    <Stack>
      <Typography variant="h6" gutterBottom>
        Freight Lane Information
      </Typography>
      {!isUpdate ? (
        <>
          <Stack direction="row" maxWidth="75%" spacing={2} mt={2}>
            <CustomerAutocomplete
              name="information.carrier"
              label="Carrier"
              data-test="information-carrier"
              variant="outlined"
              size="small"
              defaultValue={null}
              control={control}
              error={!!errors.information?.carrier}
              helperText={errors.information?.carrier?.message}
              fullWidth
              freeSolo={false}
              baseFilter={CUSTOMERS_FILTERS.CARRIER}
              quickAdd
              formModalProps={{ hideAll: false }}
              autocompleteProps={{
                renderOption: (props, option) => (
                  <li {...props} key={option.id}>
                    {option.displayName}
                  </li>
                ),
                isOptionEqualToValue: (option, value) => option?.id === value?.id,
              }}
            />
            <Select
              name="information.transportationType"
              label="Transportation Type"
              variant="outlined"
              size="small"
              defaultValue={TRANSPORTATION_TYPE_OPTIONS[1].id}
              control={control}
              items={TRANSPORTATION_TYPE_OPTIONS}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <Stack direction="row" maxWidth="75%" spacing={2} mt={2}>
            <Controller
              name="information.city"
              control={control}
              render={() => (
                <Autocomplete
                  variant="outlined"
                  size="small"
                  freeSolo
                  selectOnFocus
                  options={locationOptions}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Customer City"
                      error={!!errors?.information?.city}
                      helperText={errors?.information?.city?.message}
                    />
                  )}
                  sx={{ width: '100%' }}
                  onBlur={event =>
                    setValue('information.city', event.target.value || null, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  onChange={(e, option) =>
                    setValue('information.city', option || null, { shouldDirty: true, shouldValidate: true })
                  }
                />
              )}
            />
            <Select
              name="information.state"
              label="Customer State"
              variant="outlined"
              size="small"
              defaultValue=""
              control={control}
              error={!!errors?.information?.state}
              helperText={errors?.information?.state?.message}
              items={statesAndProvinces}
            />
          </Stack>
        </>
      ) : (
        <>
          <Stack direction="row" maxWidth="75%" spacing={2} mt={2}>
            <Input
              name="information.carrier"
              label="Carrier"
              data-test="information-carrier"
              variant="standard"
              size="small"
              defaultValue=""
              control={control}
              disabled
              sx={{
                'input.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.primary,
                },
                'label.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.secondary,
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Input
              name="information.transportationType"
              label="Transportation Type"
              data-test="information-transportation-type"
              variant="standard"
              size="small"
              defaultValue=""
              control={control}
              disabled
              sx={{
                'input.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.primary,
                },
                'label.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.secondary,
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>
          <Stack direction="row" maxWidth="75%" spacing={2} mt={2}>
            <Input
              name="information.fromLocation"
              label="Yard Location"
              data-test="information-from-location"
              variant="standard"
              size="small"
              defaultValue=""
              control={control}
              disabled
              sx={{
                'input.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.primary,
                },
                'label.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.secondary,
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Input
              name="information.toLocation"
              label="Customer Location"
              data-test="information-to-location"
              variant="standard"
              size="small"
              defaultValue=""
              control={control}
              disabled
              sx={{
                'input.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.primary,
                },
                'label.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.secondary,
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Stack>
        </>
      )}
    </Stack>
  )
}

FreightLaneBladeInformation.propTypes = {
  isUpdate: PropTypes.bool,
}

FreightLaneBladeInformation.defaultProps = {
  isUpdate: false,
}

export default FreightLaneBladeInformation
